import React from 'react';
import { camelize, camelizeKeys } from 'humps';
import {
  core_apimessages_AddressEntry as AddressEntry,
} from '../schemas/rql';
import { ICountry } from '../schemas/core-schema';

import RegionSelect from './addresses/region_select';
import {
  AddressInputField,
  LabelOverride,
  Field,
} from '../lib/address_form_fields_types';
import { buildInputName } from '../lib/address_form_fields_helpers';

import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';
import FormGroup from '@reverbdotcom/commons/src/components/form_group';

type PhoneLabelTooltip = JSX.Element | React.ReactNode;
type UpdateFieldCallback = (addressFields: any) => any;

export interface Props {
  field: Field;
  fieldPrefix: string;
  user: Partial<IUser>;
  address: AddressEntry;
  countries: ICountry[];
  updateField: UpdateFieldCallback;
  requiredFieldsOverrides?: AddressInputField[];
  disabledFieldsOverrides?: AddressInputField[];
  labelOverrides: LabelOverride;
  phoneLabelTooltip: PhoneLabelTooltip;
  useCamelizedFieldsForRQL?: boolean;
  disabled?: boolean;
}

function camelizedCountries(countries) {
  return countries.map(c => camelizeKeys(c)) as ICountry[];
}

function handleInputChange(
  event: any,
  address: any,
  updateField: UpdateFieldCallback,
  useCamelizedFieldsForRQL: boolean,
) {
  if (useCamelizedFieldsForRQL) {
    updateField({ ...address, ...camelizeKeys(event) });
  } else {
    const name = Object.keys(event)[0];

    updateField({ ...address, [name]: event[name] });
  }
}

function handleRegionChange(
  region: string,
  address: AddressEntry,
  updateField,
) {
  updateField({ ...address, region });
}

function getFieldLabel(
  { fieldName, display }: Field,
  user: Partial<IUser>,
  labelOverrides: LabelOverride,
  phoneLabelTooltip: PhoneLabelTooltip,
) {
  if (fieldName === 'phone' && !!phoneLabelTooltip) {
    return phoneLabelTooltip;
  }

  return buildLabel(fieldName, display[user.locale], labelOverrides);
}

function buildLabel(fieldName: string, fieldLabel: string, labelOverrides: LabelOverride) {
  return labelOverrides[fieldName] || fieldLabel;
}

export function AddressFormField({
  field,
  fieldPrefix,
  user,
  countries,
  address,
  useCamelizedFieldsForRQL,
  updateField,
  labelOverrides,
  phoneLabelTooltip,
  requiredFieldsOverrides = [],
  disabledFieldsOverrides = [],
  disabled,
}: Props) {
  if (field.fieldName === 'region') {
    return (
      <FormGroup
        key={field.fieldName}
        label={field.display[user.locale]}
      >
        <RegionSelect
          name={buildInputName(fieldPrefix, 'region')}
          countries={camelizedCountries(countries)}
          value={address.region}
          countryCode={address.countryCode}
          onChange={(event) => handleRegionChange(event.target.value, address, updateField)}
          disabled={disabled || disabledFieldsOverrides.includes('region')}
        />
      </FormGroup>
    );
  }

  return (
    <FormGroupWithInput
      key={field.fieldName}
      required={field.required || requiredFieldsOverrides.includes(field.fieldName)}
      label={getFieldLabel(field, user, labelOverrides, phoneLabelTooltip)}
      inputName={buildInputName(fieldPrefix, field.fieldName)}
      fieldName={field.fieldName}
      value={address[camelize(field.fieldName)]}
      updateField={event => handleInputChange(event, address, updateField, useCamelizedFieldsForRQL)}
      tagOptional={!field.required && !requiredFieldsOverrides.includes(field.fieldName)}
      disabled={disabled || disabledFieldsOverrides.includes(field.fieldName)}
    />
  );
}

export default AddressFormField;
